import { useLayoutEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";
import { breadcrumbList } from "../seo/breadcrumbSchema";
import { apiUrl } from "../utils/apiBase";

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    topic: "General Inquiry",
    message: "",
  });

  useLayoutEffect(() => {
    if (searchParams.get("inquiry") !== "AMR Demo") return;
    setFormData((current) => ({ ...current, topic: "AMR Demo" }));
  }, [searchParams]);
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const key = e.target.id || e.target.name;
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("Sending...");

    try {
      const res = await fetch(apiUrl("/api/contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        setStatus("Message sent! MAG will be in touch shortly.");
        if (window.gtag) {
          window.gtag("event", "generate_lead", {
            form_name: "contact",
            topic: formData.topic,
            page_path: window.location.pathname,
          });
        }
        setFormData({ name: "", email: "", company: "", phone: "", topic: "General Inquiry", message: "" });
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error("Error:", errorData);
        setStatus("Failed to send. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      setStatus("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-950 text-white">
      <Seo
        title="Contact MAG | AGV and AMR Integration, Metro Detroit"
        description="Talk to the MAG team about scoping an AMR or AGV program, booking a MAG Lab demo, or a free proof of concept. Metro Detroit based."
        path="/contact"
        jsonLd={breadcrumbList([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
      />

      <section className="flex min-h-[calc(100svh-4.75rem)] flex-col">
        <div className="grid flex-1 lg:grid-cols-2">
          <div className="flex flex-col justify-center px-5 py-10 sm:px-8 lg:px-12 xl:px-16">
            <p className="page-eyebrow-on-dark">Inquiry</p>
            <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">Send MAG a message</h2>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-white/60">
              A few lines on the project and where MAG can help.
            </p>

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div>
                <label htmlFor="name" className="sr-only">
                  Full Name
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Full Name"
                  className="field-input-on-dark"
                />
              </div>
              <div>
                <label htmlFor="email" className="sr-only">
                  Work Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Work Email"
                  className="field-input-on-dark"
                />
              </div>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="company" className="sr-only">
                    Company
                  </label>
                  <input
                    id="company"
                    type="text"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Company"
                    className="field-input-on-dark"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="sr-only">
                    Phone
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone"
                    className="field-input-on-dark"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="topic" className="sr-only">
                  Topic
                </label>
                <select
                  id="topic"
                  name="topic"
                  value={formData.topic}
                  onChange={handleChange}
                  className="field-input-on-dark bg-gray-950"
                >
                  <option value="General Inquiry">General Inquiry</option>
                  <option value="AMR Demo">AMR Demo</option>
                  <option value="Robotics & Automation">AMR and AGV Integration</option>
                  <option value="Systems Integration">MAG Lab Demo or Free PoC</option>
                  <option value="Software & Analytics">MAG Insight™</option>
                  <option value="Support">Support</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="sr-only">
                  How can MAG help?
                </label>
                <textarea
                  id="message"
                  rows="3"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  placeholder="How can MAG help?"
                  className="field-input-on-dark resize-none"
                />
              </div>
              <div className="flex flex-col items-start gap-3 pt-2 sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full disabled:opacity-60 sm:w-auto sm:min-w-[12rem]"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
                {status ? (
                  <span
                    className={`text-sm font-medium ${
                      status.includes("sent")
                        ? "text-white"
                        : status.includes("Failed") || status.includes("error")
                        ? "text-red-400"
                        : "text-white/60"
                    }`}
                  >
                    {status}
                  </span>
                ) : null}
              </div>
            </form>
          </div>

          <div className="order-first flex flex-col justify-center bg-black px-5 py-10 sm:px-8 lg:order-none lg:px-12 xl:px-16">
            <h1 className="max-w-lg text-5xl font-bold tracking-tight sm:text-6xl lg:text-7xl xl:text-8xl xl:leading-[0.9]">
              Contact MAG
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-white/70 sm:text-lg">
              Describe the floor and what needs to move. MAG will come back with a direct answer on fit.
            </p>
          </div>
        </div>

        <div className="grid gap-8 border-t border-white/10 px-5 py-6 sm:px-8 md:grid-cols-3 lg:px-12 xl:px-16">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">
              Talk to the Team
            </p>
            <p className="mt-2 text-sm text-white/70">
              Scoping conversations, MAG Lab demos, and free PoC requests all start here.
            </p>
            <a
              href="mailto:solutions@mag-automate.com"
              className="mt-2 inline-block text-sm font-medium text-white underline-offset-4 hover:underline"
            >
              solutions@mag-automate.com
            </a>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">
              Location
            </p>
            <p className="mt-2 text-sm text-white/70">Metro Detroit HQ. Serving North American manufacturers.</p>
            <p className="mt-1 text-sm font-medium text-white">35572 Veronica St, Livonia, MI 48150</p>
            <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">
              Business Hours
            </p>
            <p className="mt-1 text-sm text-white/70">Monday to Friday</p>
            <p className="text-sm font-medium text-white">7:30 AM to 5:30 PM ET</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/45">
              Prefer to email directly?
            </p>
            <p className="mt-2 text-sm text-white/70">
              Reach the MAG team anytime for a response within one business day.
            </p>
            <a
              href="mailto:info@mag-automate.com"
              className="mt-2 inline-block text-sm font-medium text-white underline-offset-4 hover:underline"
            >
              info@mag-automate.com
            </a>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact MAG",
            url: "https://www.mag-automate.com/contact",
            mainEntity: {
              "@type": "Organization",
              name: "Manufacturing Automation Group",
              url: "https://www.mag-automate.com/",
              logo: "https://www.mag-automate.com/mag-logo-hd.png",
              address: {
                "@type": "PostalAddress",
                streetAddress: "35572 Veronica St",
                addressLocality: "Livonia",
                addressRegion: "MI",
                postalCode: "48150",
                addressCountry: "US",
              },
              contactPoint: [
                {
                  "@type": "ContactPoint",
                  contactType: "sales",
                  email: "solutions@mag-automate.com",
                  areaServed: "US",
                  availableLanguage: ["English"],
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}
